import type { AdminUsersFacets } from "@/api/custom_types";
import { facetCounter, toFacetFilterOptions } from "@/components";
import { useAutoApplyFilters } from "@/hooks/useAutoApplyFilters";
import type { FacetCounts } from "@/hooks/useFacetFilters";
import { useTranslation } from "@/hooks/useTranslation";
import {
	Card,
	CardContent,
	type FieldConfig,
	type FilterConfig,
	GenericFilters,
} from "@st-marketplace/ui";
import { useMemo } from "react";

export type LocalCommissionFilter = "" | "custom" | "inherited";

export interface AdminUsersFilters {
	search: string;
	proxyOwnerTypes: string[];
	localCommission: LocalCommissionFilter;
}

export const emptyAdminUsersFilters = (): AdminUsersFilters => ({
	search: "",
	proxyOwnerTypes: [],
	localCommission: "",
});

interface FilterFormProps {
	onSearch: (values: AdminUsersFilters) => void;
	defaultValues: AdminUsersFilters;
	facetCounts: FacetCounts<AdminUsersFacets>;
}

export const STAdminUsersFilterForm: React.FC<FilterFormProps> = ({
	onSearch,
	defaultValues,
	facetCounts,
}) => {
	const { t } = useTranslation();

	const { filters, onFiltersChange, clearFilters } =
		useAutoApplyFilters<AdminUsersFilters>({
			initialFilters: defaultValues,
			onApply: onSearch,
			textFilterKeys: ["search"],
		});

	const proxyOptions = useMemo(
		() =>
			toFacetFilterOptions(facetCounts.proxy_owner_types, (type) =>
				t(
					type === "SYSTEM"
						? "admin.users.proxyShared"
						: "admin.users.proxyPersonal",
				),
			),
		[facetCounts.proxy_owner_types, t],
	);

	const commissionOptions = useMemo(
		() =>
			(facetCounts.local_commission ?? [])
				.filter((f) => f.value != null)
				.map((f) => {
					const isCustom = f.value === true;
					return {
						value: (isCustom ? "custom" : "inherited") as
							| "custom"
							| "inherited",
						label: t(
							isCustom
								? "admin.users.commissionFilterCustom"
								: "admin.users.commissionFilterInherited",
						),
						counter: facetCounter(f.filtered, f.total),
					};
				}),
		[facetCounts.local_commission, t],
	);

	const config = useMemo<FilterConfig<AdminUsersFilters>>(() => {
		const fields: FieldConfig<AdminUsersFilters>[] = [
			{
				key: "search",
				type: "search",
				placeholder: t("admin.users.filterSearch"),
			},
			{
				key: "proxyOwnerTypes",
				type: "multi-select",
				placeholder: t("admin.users.proxyType"),
				options: proxyOptions,
			},
			{
				key: "localCommission",
				type: "select",
				placeholder: t("admin.users.commissionFilter"),
				options: commissionOptions,
				searchable: false,
			},
		];

		return { fields, searchDebounceMs: 0, showClearButton: true };
	}, [t, proxyOptions, commissionOptions]);

	return (
		<Card className="w-full">
			<CardContent>
				<GenericFilters
					config={config}
					filters={filters}
					onFiltersChange={onFiltersChange}
					onClearFilters={() => clearFilters(emptyAdminUsersFilters())}
				/>
			</CardContent>
		</Card>
	);
};
